import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import { SearchAndFilterBar } from '../../components/search-and-filter-bar';
import ActiveFilters from '../users-page/components/ActiveFilters';
import CustomersTable from './components/CustomersTable';
import ResponseModal from '../../components/modals/response-modal';
import FilesViewerModal from '../../components/modals/files-viewer/FilesViewerModal';
import EmptyState from '../../components/not-result';
import SEOHead from '../../components/SEO/SEOHead';
import customerService from '../../services/customerService';
import AddForm from '../../components/modals/add-item';
import { getUserRole, canEditCustomers, canCreateCustomers } from '../../utils/roleAccess';

const ITEMS_PER_PAGE = 10;

const normalizeCustomer = (rawCustomer = {}) => {
  const customer = rawCustomer.data || rawCustomer;
  return {
    _id: customer._id || customer.id,
    id: customer._id || customer.id,
    companyName: customer.companyName || "",
    customerAddress: customer.customerAddress || {
      address: "",
      city: "",
      state: "",
      zipCode: "",
    },
    email: customer.email || "",
    phoneNumber: customer.phoneNumber || "",
    loads: customer.loads || [],
    pdfs: customer.pdfs || [],
    status: customer.status || "active",
    paymentMethod: customer.paymentMethod || "",
    paymentTerms: customer.paymentTerms || "",
    creditLimit: customer.creditLimit || 0,
    representativePeoples: Array.isArray(customer.representativePeoples) 
      ? customer.representativePeoples 
      : [],
    allowedUsers: Array.isArray(customer.allowedUsers) 
      ? customer.allowedUsers 
      : [],
    createdAt: customer.createdAt ? new Date(customer.createdAt) : new Date(),
    updatedAt: customer.updatedAt ? new Date(customer.updatedAt) : new Date(),
    expanded: false,
    name: customer.companyName || "",
    city: customer.customerAddress?.city || "",
    state: customer.customerAddress?.state || "",
    address: customer.customerAddress?.address || "",
    zipCode: customer.customerAddress?.zipCode || "",
  };
};

const CustomersPage = () => {
  const { getThemeColors } = useTheme();
  const { user } = useUser();
  const colors = getThemeColors();
  const role = getUserRole(user);
  const canEdit = canEditCustomers(role);
  const canCreate = canCreateCustomers(role);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: '',
    status: 'success',
    title: ''
  });

  // Files viewer modal state
  const [filesModal, setFilesModal] = useState({
    isOpen: false,
    files: [],
    entityName: ''
  });

  const showResponseModal = (message, status = 'success', title = '') => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title
    });
  };

  const closeResponseModal = () => {
    setResponseModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleViewFiles = (customer) => {
    setFilesModal({
      isOpen: true,
      files: customer.pdfs || [],
      entityName: customer.companyName || customer.name
    });
  };

  const closeFilesModal = () => {
    setFilesModal({ isOpen: false, files: [], entityName: '' });
  };

  // Use ref to prevent multiple simultaneous requests
  const isFetchingRef = useRef(false);
  const abortControllerRef = useRef(null);

  const buildFilterParams = (values = {}) => {
    const params = {};
    const setIf = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim() === '') return;
      params[key] = value;
    };

    setIf('companyName', values.companyName);
    setIf('email', values.email);
    setIf('phoneNumber', values.phoneNumber);
    setIf('city', values.city);
    setIf('state', values.state);
    setIf('status', values.status);
    setIf('type', values.type);
    setIf('paymentMethod', values.paymentMethod);
    setIf('paymentTerms', values.paymentTerms);
    setIf('orderId', values.orderId);
    setIf('address.address', values['address.address']);
    setIf('zipCode', values.zipCode);

    if (values.createdDate) {
      params.createdAt = values.createdDate;
    }
    if (values.createdAt) {
      params.createdAt = values.createdAt;
    }

    return params;
  };

  const fetchCustomers = useCallback(async (page = currentPage, search = searchTerm, filters = filterValues) => {
    if (isFetchingRef.current) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;
    setLoading(true);
    setFetchError(null);

    try {
      const params = {
        page: page,
        limit: ITEMS_PER_PAGE,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (search.trim()) {
        params.search = search.trim();
      }

      const filterParams = buildFilterParams(filters);
      const hasFilters = Object.keys(filterParams).length > 0;
      Object.assign(params, filterParams);

      let response;
      if (search.trim() || hasFilters) {
        const searchParams = {
          ...params,
          ...(search.trim() && { q: search.trim() }),
        };
        response = await customerService.searchCustomers(searchParams);
      } else {
        response = await customerService.getCustomers(params);
      }

      const apiResponse = response?.data || response;
      const rawCustomers = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const pagination = apiResponse?.pagination || apiResponse?.meta || {};

      if (!Array.isArray(rawCustomers) || rawCustomers.length === 0) {
        console.warn("No customers found in API response");
        setCustomers([]);
        setTotalItems(0);
        isFetchingRef.current = false;
        return;
      }

      const normalizedCustomers = rawCustomers.map(normalizeCustomer);
      const total = pagination.total ?? pagination.totalItems ?? rawCustomers.length;
      const totalPages =
        pagination.totalPages ??
        (total > 0 ? Math.ceil(total / ITEMS_PER_PAGE) : 1);

      if (totalPages > 0 && page > totalPages) {
        setCurrentPage(totalPages);
        isFetchingRef.current = false;
        return;
      }

      setCustomers(normalizedCustomers);
      setTotalItems(total);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        return;
      }
      console.error("Error fetching customers:", err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to fetch customers";
      if (errorMessage.includes("Invalid ID format") || errorMessage.includes("CastError")) {
        setFetchError("No customers found matching your search criteria");
      } else {
        setFetchError(errorMessage);
      }
      setCustomers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      abortControllerRef.current = null;
    }
  }, []);

  const handleSearchSubmit = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, searchTerm, filterValues, fetchCustomers]);

  const handleFilterApply = (filters) => {
    setFilterValues(filters);
    
    // Convert filter object to array of filter strings for ActiveFilters display
    const filterArray = [];
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          filterArray.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'boolean' && value) {
          filterArray.push(key);
        } else if (typeof value === 'string' && value.trim() !== '') {
          filterArray.push(`${key}: ${value}`);
        }
      }
    });
    setActiveFilters(filterArray);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterIndex) => {
    const filterToRemove = activeFilters[filterIndex];
    const newFilters = activeFilters.filter((_, index) => index !== filterIndex);
    setActiveFilters(newFilters);
    
    // Also update filterValues
    const updatedFilterValues = { ...filterValues };
    const key = filterToRemove.split(':')[0].trim();
    if (updatedFilterValues[key] !== undefined) {
      if (Array.isArray(updatedFilterValues[key])) {
        updatedFilterValues[key] = [];
      } else {
        updatedFilterValues[key] = '';
      }
      setFilterValues(updatedFilterValues);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilterValues({});
    setCurrentPage(1);
  };

  const handleSelectAll = (pageItemIds = []) => {
    if (pageItemIds.length === 0) {
      setSelectedCustomers([]);
      return;
    }

    const hasSelectedAll = pageItemIds.every((id) =>
      selectedCustomers.includes(id)
    );

    if (hasSelectedAll) {
      setSelectedCustomers((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedCustomers((prev) => {
        const merged = new Set(prev);
        pageItemIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    }
  };

  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleDeleteSelected = async (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) {
      if (selectedCustomers.length === 0) return;
      idsToDelete = selectedCustomers;
    }

    try {
      setLoading(true);
      await Promise.all(
        idsToDelete.map((id) => customerService.deleteCustomer(id))
      );
      
      showResponseModal(
        `Successfully deleted ${idsToDelete.length} customer(s)`,
        'success',
        'Deleted'
      );
      await fetchCustomers();
      setSelectedCustomers([]);
    } catch (err) {
      console.error("Error deleting customers:", err);
      showResponseModal(
        err?.response?.data?.error || err?.message || "Failed to delete customers",
        'error',
        'Delete Failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (customerId) => {
    setCustomers(prev => prev.map(customer => 
      (customer._id || customer.id) === customerId 
        ? { ...customer, expanded: !customer.expanded }
        : customer
    ));
  };


  return (
    <>
      <SEOHead
        title="Customer Management"
        description="Manage your customer relationships and information. Track customer details, payment terms, credit limits, and contact information efficiently."
        keywords="customer management, client management, customer database, logistics customers, customer tracking, client relationships"
        url="/customers"
      />
      <Container fluid style={{ backgroundColor: colors.background, minHeight: '100vh' }}>
      <SearchAndFilterBar
        searchTerm={searchInputValue}
        onSearchChange={setSearchInputValue}
        onSearchSubmit={handleSearchSubmit}
        onFilterApply={handleFilterApply}
        pageType="customers"
        initialFilters={filterValues}
        showAddButton={canCreate}
        onAddSuccess={() => {
          showResponseModal('Customer created successfully', 'success', 'Success');
          fetchCustomers();
        }}
      />

      {activeFilters.length > 0 && (
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      {fetchError ? (
        <EmptyState
          variant="error"
          title="Failed to load data"
          message={fetchError}
          onRetry={() => fetchCustomers()}
          retryText="Retry"
        />
      ) : customers.length === 0 && !loading ? (
        <EmptyState
          variant={searchTerm || Object.keys(filterValues).length > 0 ? "search" : "empty"}
          title={searchTerm || Object.keys(filterValues).length > 0 ? "No results found" : "No customers yet"}
          message={
            searchTerm && Object.keys(filterValues).length > 0
              ? `No customers matching "${searchTerm}" with the selected filters`
              : searchTerm
              ? `No customers matching "${searchTerm}"`
              : Object.keys(filterValues).length > 0
              ? "No customers match the selected filters"
              : "Add your first customer to get started"
          }
        />
      ) : (
        <CustomersTable
          customers={customers}
          selectedCustomers={selectedCustomers}
          onSelectAll={handleSelectAll}
          onSelectCustomer={handleSelectCustomer}
          onToggleExpand={handleToggleExpand}
          onDeleteSelected={handleDeleteSelected}
          onEdit={(customer) => {
            if (!canEdit) return;
            setEditCustomerData(customer);
            setIsEditModalOpen(true);
          }}
          onViewFiles={handleViewFiles}
          canSelect={canEdit}
          canDelete={canEdit}
          canEdit={canEdit}
          canChangeStatus={canEdit}
          onChangeStatus={async (id, status) => {
            try {
              await customerService.updateCustomer(id, { status });
              showResponseModal(
                `Customer status updated to ${status}`,
                'success',
                'Status Updated'
              );
              await fetchCustomers();
            } catch (error) {
              showResponseModal(
                error?.response?.data?.error || error?.message || `Failed to update customer status`,
                'error',
                'Update Failed'
              );
            }
          }}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
          loading={loading}
        />
      )}


      {isEditModalOpen && editCustomerData && (
        <AddForm
          key="edit-customer-modal"
          isOpen={isEditModalOpen}
          setIsOpen={setIsEditModalOpen}
          formType="customers"
          userData={{
            companyName: editCustomerData.companyName,
            email: editCustomerData.email || '',
            message: editCustomerData.phoneNumber || '',
            customerAddress: editCustomerData.customerAddress?.address || editCustomerData.address || '',
            customerCity: editCustomerData.customerAddress?.city || editCustomerData.city || '',
            customerState: editCustomerData.customerAddress?.state || editCustomerData.state || '',
            customerZipCode: editCustomerData.customerAddress?.zipCode || editCustomerData.zipCode || '',
            paymentMethod: editCustomerData.paymentMethod || '',
            paymentTerms: editCustomerData.paymentTerms || '',
            creditLimit: editCustomerData.creditLimit || 0,
            representativePeoples: editCustomerData.representativePeoples || [],
            allowedUsers: editCustomerData.allowedUsers || [],
            pdfs: editCustomerData.pdfs || []
          }}
          isEditMode={true}
          itemId={editCustomerData._id || editCustomerData.id}
          onSuccess={() => {
            showResponseModal('Customer updated successfully', 'success', 'Success');
            fetchCustomers();
            setIsEditModalOpen(false);
            setEditCustomerData(null);
          }}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditCustomerData(null);
          }}
        />
      )}

      <ResponseModal
        isOpen={responseModal.isOpen}
        message={responseModal.message}
        status={responseModal.status}
        title={responseModal.title}
        onClose={closeResponseModal}
      />

      <FilesViewerModal
        isOpen={filesModal.isOpen}
        onClose={closeFilesModal}
        files={filesModal.files}
        title="Customer Documents"
        entityName={filesModal.entityName}
      />
    </Container>
    </>
  );
};

export default CustomersPage;
